import type { VendorScheduleItem } from '@goodfynd/types';
import type { VendorSearchItem } from 'types/vendors';

import { differenceInDays } from './date-util';
import * as dateUtil from './date-util';

type CoordinatesLike = {
  latitude?: number;
  longitude?: number;
  values?: number[];
};

const toNumberOrUndefined = (value?: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  return undefined;
};

const getLatitude = (coordinates?: CoordinatesLike): number | undefined => {
  if (!coordinates) {
    return undefined;
  }

  return (
    toNumberOrUndefined(coordinates.latitude) ??
    toNumberOrUndefined(coordinates.values?.[1])
  );
};

const getLongitude = (coordinates?: CoordinatesLike): number | undefined => {
  if (!coordinates) {
    return undefined;
  }

  return (
    toNumberOrUndefined(coordinates.longitude) ??
    toNumberOrUndefined(coordinates.values?.[0])
  );
};

const toItemLocation = (appointment: VendorSearchItem): ItemLocation => {
  const coordinatesSource =
    appointment.scheduleLocation?.coordinates ??
    appointment.location?.coordinates;

  const latitude = getLatitude(coordinatesSource);
  const longitude = getLongitude(coordinatesSource);
  const hasValidCoordinates = latitude !== undefined && longitude !== undefined;

  return {
    address: appointment.scheduleAddress,
    coordinates: hasValidCoordinates ? `${latitude},${longitude}` : '',
    directions: appointment.scheduleAddress,
    isPrivate: appointment.scheduleIsPrivate ?? undefined,
    isValid: hasValidCoordinates,
    label: appointment.scheduleName,
    latitude: latitude ?? 0,
    longitude: longitude ?? 0,
    name: appointment.scheduleName,
  };
};

const toOperatingDayLocation = (
  appointment: VendorSearchItem
): OperatingDayLocation => {
  const location = toItemLocation(appointment);
  const startDate = new Date(appointment.scheduleStart);
  const endDate = new Date(appointment.scheduleEnd);

  return {
    directions: appointment.scheduleAddress,
    end: dateUtil.isValid(endDate) ? endDate : appointment.scheduleEnd,
    hasLocation: Boolean(location.isValid || appointment.scheduleAddress),
    id: appointment.id,
    image: appointment.image,
    isDeleted: appointment.scheduleIsDeleted,
    isPrivate: appointment.scheduleIsPrivate,
    location,
    lotId: appointment.lotId,
    pickupCapacity: appointment.schedulePickupCapacity,
    productGroupId: appointment.scheduleProductGroupId,
    start: dateUtil.isValid(startDate) ? startDate : appointment.scheduleStart,
    vendorId: appointment.vendorId,
    vendorNameId: appointment.vendorNameId,
  };
};

export const appointmentsToOperatingDayLocations = (
  appointments: VendorSearchItem[] = []
): OperatingDayLocation[] => {
  return appointments.reduce<OperatingDayLocation[]>(
    (accumulator, appointment) => {
      if (!appointment) {
        return accumulator;
      }

      if (!appointment.scheduleStart || !appointment.scheduleEnd) {
        return accumulator;
      }

      accumulator.push(toOperatingDayLocation(appointment));
      return accumulator;
    },
    []
  );
};

export const canScheduleOrder = (
  appointment: VendorScheduleItem | VendorSearchItem
): boolean => {
  return (
    appointment.schedulePickupCapacity > 0 &&
    differenceInDays(appointment.scheduleStart, new Date()) <= 5
  );
};

export const getCurrentOrNextEvent = (
  locations: OperatingDayLocation[] = [],
  referenceDate: Date = new Date()
): OperatingDayLocation | undefined => {
  if (!locations.length) {
    return undefined;
  }

  const validLocations = locations.filter(
    (location) => !location.isDeleted && !location.isPrivate
  );

  if (!validLocations.length) {
    return undefined;
  }

  const toValidDate = (value: string | Date): Date | undefined => {
    const date = value instanceof Date ? value : new Date(value);
    return dateUtil.isValid(date) ? date : undefined;
  };

  const sortedLocations = [...validLocations].sort((left, right) => {
    const leftStart =
      toValidDate(left.start)?.getTime() ?? Number.POSITIVE_INFINITY;
    const rightStart =
      toValidDate(right.start)?.getTime() ?? Number.POSITIVE_INFINITY;

    return leftStart - rightStart;
  });

  const currentOrUpcomingToday = sortedLocations.find((location) => {
    const start = toValidDate(location.start);
    const end = toValidDate(location.end);

    if (!start || !dateUtil.isSameDay(start, referenceDate)) {
      return false;
    }

    if (!end) {
      return start.getTime() >= referenceDate.getTime();
    }

    return start.getTime() <= referenceDate.getTime()
      ? end.getTime() >= referenceDate.getTime()
      : start.getTime() >= referenceDate.getTime();
  });

  if (currentOrUpcomingToday) {
    return currentOrUpcomingToday;
  }

  return sortedLocations.find((location) => {
    const start = toValidDate(location.start);
    return !!start && start.getTime() > referenceDate.getTime();
  });
};
